import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { config } from "../../config/config";

function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // In a real app, you would use an admin API with authentication
      const response = await fetch(`${config.api.baseURL}/admin/dashboard`);
      const data = await response.json();
      setStats(data.stats);
      setActivities(data.recent_activities);
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome to Innobig CMS Admin Panel</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <span className="text-2xl">📄</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Pages</p>
                <p className="text-2xl font-bold">{stats.pages || 0}</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/admin/pages"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Manage Pages →
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <span className="text-2xl">🎓</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Programs</p>
                <p className="text-2xl font-bold">{stats.programs || 0}</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/admin/programs"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Manage Programs →
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <span className="text-2xl">📝</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Blog Posts</p>
                <p className="text-2xl font-bold">{stats.blog_posts || 0}</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/admin/blog"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Manage Blog →
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <span className="text-2xl">📧</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Subscribers</p>
                <p className="text-2xl font-bold">{stats.subscribers || 0}</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/admin/subscribers"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Subscribers →
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Activities</h2>
          </div>
          <div className="divide-y">
            {activities.length > 0 ? (
              activities.map((activity, index) => (
                <div key={index} className="px-6 py-4 flex items-center">
                  <div
                    className={`p-2 rounded-full ${getActivityColor(
                      activity.type,
                    )}`}
                  >
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="ml-4">
                    <p className="font-medium">{activity.title}</p>
                    <p className="text-sm text-gray-500 capitalize">
                      {activity.type}
                    </p>
                  </div>
                  <div className="ml-auto text-sm text-gray-500">
                    {new Date(activity.updated_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                No recent activities
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/admin/pages/new"
                className="block w-full text-center bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
              >
                + Add New Page
              </Link>
              <Link
                to="/admin/programs/new"
                className="block w-full text-center bg-green-600 text-white py-2 rounded hover:bg-green-700"
              >
                + Add New Program
              </Link>
              <Link
                to="/admin/blog/new"
                className="block w-full text-center bg-[#3B82F6] text-white py-2 rounded hover:bg-[#1F3A5F]"
              >
                + Add New Blog Post
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Homepage Management</h3>
            <div className="space-y-3">
              <Link
                to="/admin/home/hero"
                className="block text-blue-600 hover:text-blue-800"
              >
                Edit Hero Section
              </Link>
              <Link
                to="/admin/home/programs"
                className="block text-blue-600 hover:text-blue-800"
              >
                Manage Featured Programs
              </Link>
              <Link
                to="/admin/home/services"
                className="block text-blue-600 hover:text-blue-800"
              >
                Update Services
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Site Settings</h3>
            <div className="space-y-3">
              <button className="block w-full text-left text-gray-700 hover:text-blue-600">
                General Settings
              </button>
              <button className="block w-full text-left text-gray-700 hover:text-blue-600">
                SEO Configuration
              </button>
              <button className="block w-full text-left text-gray-700 hover:text-blue-600">
                Newsletter Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function getActivityIcon(type) {
  switch (type) {
    case "page":
      return "📄";
    case "program":
      return "🎓";
    case "blog":
      return "📝";
    default:
      return "📌";
  }
}

function getActivityColor(type) {
  switch (type) {
    case "page":
      return "bg-blue-100";
    case "program":
      return "bg-green-100";
    case "blog":
      return "bg-purple-100";
    default:
      return "bg-gray-100";
  }
}

export default AdminDashboard;
